import { useEffect } from "react";
import Lenis from "lenis";
import { motion } from "framer-motion";
import { Zap, ShieldCheck, Clock, Wallet, Hammer, Home, Droplets, Paintbrush, Trees, Sparkles, Truck, HardHat } from "lucide-react";
import SkylineBackground from "./SkylineBackground";
import TopProsStrip from "./TopProsStrip";
import AdSpot from "./AdSpot";
import ReferralPromo from "./ReferralPromo";
import HeroVideo from "./HeroVideo";
import ReferralLeaderboard from "./ReferralLeaderboard";
import { ForceLogo } from "./ForceLogo";

const CATEGORIES = ["Electrical", "Yard Work", "Plumbing", "Carpentry", "Painting", "Moving", "Cleaning"];

const STATS = [
    { icon: Zap, value: "$34k+", label: "Jobs near you" },
    { icon: ShieldCheck, value: "12,400+", label: "Verified pros" },
    { icon: Clock, value: "4.2 min", label: "Avg response" },
    { icon: Wallet, value: "100%", label: "Secure payout" },
];

const CHAPTERS = [
    { n: "01", title: "Post the job", body: "Describe the side job at your home in under 60 seconds. Photos optional, address auto-filled." },
    { n: "02", title: "Shake on it", body: "Local, verified workers bid in real time. Pick by rating, price, and how fast they can show up." },
    { n: "03", title: "Get it done, get paid", body: "Payment is held safely and released the moment you sign off on the finished job. Zero awkward cash." },
];

const SERVICES = [
    { icon: Zap, title: "Electrical", blurb: "Outlets, fixtures, panels" },
    { icon: Droplets, title: "Plumbing", blurb: "Leaks, faucets, drains" },
    { icon: Hammer, title: "Carpentry", blurb: "Decks, trim, repairs" },
    { icon: Paintbrush, title: "Painting", blurb: "Interior & exterior" },
    { icon: Trees, title: "Yard Work", blurb: "Mowing, cleanups, hedges" },
    { icon: Sparkles, title: "Cleaning", blurb: "Deep & move-out cleans" },
    { icon: Truck, title: "Moving", blurb: "Hauling & heavy lifting" },
    { icon: HardHat, title: "Roofing", blurb: "Gutters & repairs" },
];

const HERO_LINES = ["Let the", "FORCE assist you."];

// Shared marketing shell used by both the logged-out landing page (right slot =
// auth card) and the logged-in home (right slot = live jobs map). `header` is the
// page-specific top bar, `rightSlot` is whatever fills the hero's right column.
export default function LandingShell({ header, rightSlot }) {
    useEffect(() => {
        const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
        let raf;
        function loop(t) { lenis.raf(t); raf = requestAnimationFrame(loop); }
        raf = requestAnimationFrame(loop);
        return () => { cancelAnimationFrame(raf); lenis.destroy(); };
    }, []);

    return (
        <div className="relative min-h-screen text-white">
            <SkylineBackground />

            {header}

            {/* Hero + right slot */}
            <section className="relative z-10 mx-auto grid max-w-7xl grid-cols-1 md:grid-cols-2 gap-10 md:gap-8 px-5 sm:px-10 pt-6 md:pt-16 pb-24 md:items-start">
                {/* Left */}
                <div className="order-2 md:order-1 md:sticky md:top-24">
                    <motion.span
                        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                        className="inline-flex items-center gap-2 rounded-full border border-orange-500/30 bg-orange-500/5 px-4 py-1.5 text-[11px] font-mono uppercase tracking-[0.2em] text-orange-300"
                    >
                        The home-jobs marketplace
                    </motion.span>

                    <h1 className="mt-6 font-display text-4xl sm:text-5xl md:text-6xl font-black uppercase leading-[0.94] tracking-tight">
                        {HERO_LINES.map((line, i) => (
                            <span key={i} className="block overflow-hidden">
                                <motion.span
                                    initial={{ y: "110%" }} animate={{ y: 0 }}
                                    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.25 + i * 0.12 }}
                                    className={`block ${i === 1 ? "text-orange-500" : "text-white"}`}
                                >
                                    {line}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }} className="mt-6 max-w-md text-base text-zinc-300 leading-relaxed">
                        FORCE connects people looking for work with homeowners who need a side job done — one trusted handshake at a time.
                    </motion.p>

                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }} className="mt-6 flex flex-wrap gap-2">
                        {CATEGORIES.map((c) => (
                            <span key={c} className="rounded-full border border-white/10 bg-white/5 px-3.5 py-1.5 text-xs text-zinc-300 hover:border-orange-500/50 hover:text-white transition-colors cursor-default">
                                {c}
                            </span>
                        ))}
                    </motion.div>

                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }} className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/5">
                        {STATS.map((s) => (
                            <div key={s.label} className="bg-zinc-950/50 p-4">
                                <s.icon className="h-4 w-4 text-orange-400 mb-2" />
                                <div className="font-display text-xl font-extrabold text-white">{s.value}</div>
                                <div className="text-[11px] text-zinc-400">{s.label}</div>
                            </div>
                        ))}
                    </motion.div>
                </div>

                {/* Right — auth card OR live jobs map */}
                <div className="order-1 md:order-2 flex justify-center md:justify-end w-full">
                    {rightSlot}
                </div>
            </section>

            {/* Admin-uploaded landing video (Admin → Video) */}
            <HeroVideo variant="classic" />

            {/* Sponsored business ad — main page spot */}
            <section className="relative z-10 mx-auto max-w-7xl px-6 sm:px-10 pb-12">
                <AdSpot variant="classic" placement="landing" />
                <ReferralPromo variant="classic" />
                <ReferralLeaderboard variant="classic" />
            </section>

            {/* Marquee */}
            <div className="relative z-10 border-y border-white/10 bg-zinc-900/85 dark:bg-zinc-950/40 backdrop-blur-sm py-4 overflow-hidden">
                <motion.div className="flex whitespace-nowrap gap-8" animate={{ x: ["0%", "-50%"] }} transition={{ duration: 26, ease: "linear", repeat: Infinity }}>
                    {Array.from({ length: 2 }).map((_, k) => (
                        <div key={k} className="flex gap-8 items-center">
                            {["MOUNT A TV", "FIX A LEAK", "MOW THE LAWN", "PAINT A ROOM", "ASSEMBLE FURNITURE", "CLEAR THE GUTTERS", "HANG A LIGHT"].map((w) => (
                                <span key={w} className="font-display text-2xl font-black uppercase tracking-tight text-white/60 dark:text-white/20">
                                    {w} <span className="text-orange-500/80 dark:text-orange-500/60">✦</span>
                                </span>
                            ))}
                        </div>
                    ))}
                </motion.div>
            </div>

            <div className="relative z-10 bg-force-bg">
                {/* How it works */}
                <section className="relative z-10 mx-auto max-w-7xl px-6 sm:px-10 py-24">
                    <Reveal>
                        <div className="flex items-end justify-between mb-14">
                            <h2 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight">
                                How <span className="text-orange-500">FORCE</span> works
                            </h2>
                            <span className="hidden sm:block font-mono text-xs text-zinc-500">/ 03 CHAPTERS</span>
                        </div>
                    </Reveal>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {CHAPTERS.map((c, i) => (
                            <Reveal key={c.n} delay={i * 0.12}>
                                <div className="group relative h-full rounded-3xl border border-white/10 bg-zinc-950/50 p-7 transition-[border-color,background-color] duration-500 hover:border-orange-500/50 hover:bg-zinc-900/50">
                                    <div className="font-mono text-5xl font-black text-white/10 group-hover:text-orange-500/30 transition-colors">{c.n}</div>
                                    <h3 className="mt-4 font-display text-xl font-bold">{c.title}</h3>
                                    <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{c.body}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </section>

                <TopProsStrip />

                <section className="relative z-10 mx-auto max-w-7xl px-6 sm:px-10 pb-28">
                    <Reveal>
                        <h2 className="font-display text-3xl sm:text-4xl font-extrabold uppercase tracking-tight mb-3">
                            Any job, <span className="text-orange-500">any home</span>
                        </h2>
                        <p className="text-sm text-zinc-400 dark:text-zinc-400 mb-10 max-w-lg">
                            From a leaky tap to a full spring cleanup — post it and a verified local pro is on it.
                        </p>
                    </Reveal>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {SERVICES.map((s, i) => (
                            <Reveal key={s.title} delay={i * 0.05}>
                                <div className="group flex h-full flex-col gap-3 rounded-2xl border border-white/10 bg-zinc-950/50 p-5 transition-[border-color,background-color,transform] duration-300 transform-gpu hover:-translate-y-1 hover:border-orange-500/50 hover:bg-zinc-900/60" data-testid={`service-tile-${s.title.toLowerCase().replace(/\s+/g, "-")}`}>
                                    <span className="grid h-11 w-11 place-items-center rounded-xl border border-orange-500/25 bg-orange-500/10 text-orange-500 transition-colors duration-300 group-hover:bg-orange-500 group-hover:text-zinc-950">
                                        <s.icon className="h-5 w-5" strokeWidth={2} />
                                    </span>
                                    <div>
                                        <p className="font-display text-base font-bold leading-tight">{s.title}</p>
                                        <p className="mt-0.5 text-xs text-zinc-400 dark:text-zinc-500">{s.blurb}</p>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </section>

                <footer className="relative z-10 border-t border-white/10 px-6 sm:px-10 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <ForceLogo size={30} />
                    <p className="text-xs text-zinc-500">© {new Date().getFullYear()} FORCE. Let the FORCE assist you.</p>
                    <div className="flex gap-4 text-zinc-400">
                        <Hammer className="h-4 w-4" />
                        <Home className="h-4 w-4" />
                    </div>
                </footer>
            </div>
        </div>
    );
}

function Reveal({ children, delay = 0 }) {
    return (
        <motion.div initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay }} className="h-full">
            {children}
        </motion.div>
    );
}
